/**
 * Application entry point.
 *
 * Styles are injected once here, and the transport is told how to react to a 401
 * before any component mounts, so an expired session is handled the same way no
 * matter which request discovered it.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { configureSessionAccessors, setUnauthorizedHandler } from './api/client';
import { AppRoutes } from './app/routes';
import { createQueryClient } from './app/queryClient';
import { sessionStore } from './app/session';
import { installStyles } from './styles/theme';

installStyles();

// The transport reads the live session on every request, so a token obtained by
// signing in or a tenant chosen later is applied without any sync step.
configureSessionAccessors({
  csrfToken: () => sessionStore.getSnapshot().csrfToken,
  tenantId: () => sessionStore.getSnapshot().tenantId,
});

// Any 401 from any endpoint clears the stored CSRF token and returns to sign-in.
setUnauthorizedHandler(() => {
  sessionStore.handleUnauthorized();
});

const container = document.getElementById('root');
if (container === null) {
  throw new Error('未找到挂载节点 #root，无法启动工作台。');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={createQueryClient()}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
